import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api/v1';

const COMPRESS_MAX_DIMENSION = 1920;
const COMPRESS_QUALITY = 0.82;
const COMPRESS_MIN_SIZE_BYTES = 256 * 1024;

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(objectUrl);
      reject(err);
    };
    img.src = objectUrl;
  });

function normalizeImageFileForUpload(file) {
  if (!(file instanceof File)) return file;
  if (file.type?.startsWith('image/')) return file;
  const name = String(file.name || '').toLowerCase();
  if (/\.ico$/i.test(name)) return new File([file], file.name, { type: 'image/x-icon' });
  if (/\.svg$/i.test(name)) return new File([file], file.name, { type: 'image/svg+xml' });
  if (/\.(png|jpe?g|webp|gif)$/i.test(name)) {
    const ext = name.match(/\.(png|jpe?g|webp|gif)$/i)?.[1] || 'png';
    const mime = ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' : `image/${ext === 'jpg' ? 'jpeg' : ext}`;
    return new File([file], file.name, { type: mime });
  }
  return file;
}

async function compressImageIfNeeded(file) {
  const normalized = normalizeImageFileForUpload(file);
  if (!(normalized instanceof File)) return normalized;
  if (!normalized.type?.startsWith('image/')) return normalized;
  if (normalized.size < COMPRESS_MIN_SIZE_BYTES) return normalized;
  if (
    normalized.type === 'image/gif' ||
    normalized.type === 'image/svg+xml' ||
    normalized.type === 'image/x-icon' ||
    normalized.type === 'image/vnd.microsoft.icon'
  ) {
    return normalized;
  }

  try {
    const img = await loadImageFromFile(normalized);
    const { width, height } = img;
    const largest = Math.max(width, height);
    const scale = largest > COMPRESS_MAX_DIMENSION ? (COMPRESS_MAX_DIMENSION / largest) : 1;
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return normalized;
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    const outputType = normalized.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const ext = outputType === 'image/png' ? 'png' : 'jpg';
    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, outputType, COMPRESS_QUALITY);
    });
    if (!blob) return normalized;
    if (blob.size >= normalized.size) return normalized;
    return new File([blob], normalized.name.replace(/\.[^.]+$/, `.${ext}`), { type: outputType });
  } catch {
    return normalized;
  }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Let the browser set multipart boundary for file uploads
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    // Only force re-login on real auth failures — not during transient DB/API outages (503).
    if (status === 401 && localStorage.getItem('admin_token')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.patch('/auth/me', data),
  sendOtp: (phone) => api.post('/auth/send-otp', { phone }),
  verifyOtp: (phone, otp, extra = {}) => api.post('/auth/verify-otp', { phone, otp, ...extra })
};

export const adminAPI = {
  getDashboardStats: (params) => api.get('/admin/dashboard', { params }),
  getTrekDetailClickLeaderboard: (params) => api.get('/admin/trek-detail-clicks', { params }),
  getAllUsers: (params) => api.get('/admin/users', { params }),
  getPermissions: () => api.get('/admin/permissions'),
  updateUserRoleAndPermissions: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteAdminUser: (id) => api.delete(`/admin/users/${id}`),
  getRoles: () => api.get('/admin/roles'),
  createRole: (data) => api.post('/admin/roles', data),
  updateRole: (id, data) => api.put(`/admin/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/admin/roles/${id}`),
  getUpcomingTreksForConsultants: () => api.get('/admin/consultants/upcoming-treks'),
  getConsultantTreks: (userId) => api.get(`/admin/consultants/${userId}/treks`),
  setConsultantTreks: (userId, trekIds) => api.put(`/admin/consultants/${userId}/treks`, { trekIds }),
  getUserLeadAllocation: (userId) => api.get(`/admin/users/${userId}/lead-allocation`),
  setUserLeadAllocation: (userId, body) => api.put(`/admin/users/${userId}/lead-allocation`, body),
  getLeadAllocationOptions: (trekIds) =>
    api.get('/admin/lead-allocation/options', {
      params: { trekIds: Array.isArray(trekIds) ? trekIds.join(',') : trekIds }
    }),
  getLeadAllocPresets: (params) => api.get('/admin/lead-allocation/presets', { params }),
  createLeadAllocPreset: (data) => api.post('/admin/lead-allocation/presets', data),
  updateLeadAllocPreset: (id, data) => api.put(`/admin/lead-allocation/presets/${id}`, data),
  getSmartfloC2cTokens: (params) => api.get('/admin/smartflo/c2c-tokens', { params }),
  syncSmartfloC2cTokens: () => api.post('/admin/smartflo/c2c-tokens/sync'),
  getUserSmartfloAssignment: (userId) => api.get(`/admin/users/${userId}/smartflo`),
  setUserSmartfloAssignment: (userId, body) => api.put(`/admin/users/${userId}/smartflo`, body),
  getWaplifyTemplates: () => api.get('/admin/waplify/templates'),
  getWaplifyWebhookLogs: (params) => api.get('/integrations/waplify/webhook-logs', { params }),
  getActivityLogs: (params) => api.get('/admin/activity-logs', { params }),
  generateCustomReport: (data) => api.post('/admin/reports/generate', data),
  getReportFilterOptions: () => api.get('/admin/reports/filter-options'),
  getContactOffices: () => api.get('/contact/admin/offices'),
  createContactOffice: (data) => api.post('/contact/admin/offices', data),
  updateContactOffice: (id, data) => api.put(`/contact/admin/offices/${id}`, data),
  deleteContactOffice: (id) => api.delete(`/contact/admin/offices/${id}`),
  getContactInquiries: (params) => api.get('/contact/admin/inquiries', { params }),
  updateContactInquiryStatus: (id, status, handledBy) => api.put(`/contact/admin/inquiries/${id}/status`, { status, handledBy }),

  // About Us
  getAbout: () => api.get('/about/admin'),
  uploadImage: async (file) => {
    const normalized = normalizeImageFileForUpload(file);
    const processedFile = await compressImageIfNeeded(normalized);
    const formData = new FormData();
    formData.append('image', processedFile);
    return api.post('/uploads/images', formData);
  },
  uploadMedia: async (file) => {
    const processedFile = await compressImageIfNeeded(file);
    const formData = new FormData();
    formData.append('file', processedFile);
    return api.post('/uploads/media', formData);
  },
  uploadPdf: (file, { name } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const label = name != null ? String(name).trim() : '';
    if (label) formData.append('name', label);
    return api.post('/uploads/pdfs', formData);
  },
  /** Images, PDF, Word — for trek tips URL and similar fields */
  uploadTipsAsset: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/uploads/tips-asset', formData);
  },
  updateAboutHero: (data) => api.put('/about/admin/hero', data),
  updateAboutVmo: (data) => api.put('/about/admin/vmo', data),
  updateAboutSectionTitles: (data) => api.put('/about/admin/section-titles', data),
  updateAboutCta: (data) => api.put('/about/admin/cta', data),
  getAboutProjects: () => api.get('/about/admin/projects'),
  createAboutProject: (data) => api.post('/about/admin/projects', data),
  updateAboutProject: (id, data) => api.put(`/about/admin/projects/${id}`, data),
  deleteAboutProject: (id) => api.delete(`/about/admin/projects/${id}`),
  getAboutActivities: () => api.get('/about/admin/activities'),
  createAboutActivity: (data) => api.post('/about/admin/activities', data),
  updateAboutActivity: (id, data) => api.put(`/about/admin/activities/${id}`, data),
  deleteAboutActivity: (id) => api.delete(`/about/admin/activities/${id}`),
  getAboutJourney: () => api.get('/about/admin/journey'),
  createAboutJourney: (data) => api.post('/about/admin/journey', data),
  updateAboutJourney: (id, data) => api.put(`/about/admin/journey/${id}`, data),
  deleteAboutJourney: (id) => api.delete(`/about/admin/journey/${id}`),

  // Team (Meet Our Team)
  getTeam: () => api.get('/team/admin'),
  updateTeamHero: (data) => api.put('/team/admin/hero', data),
  updateTeamSections: (data) => api.put('/team/admin/sections', data),
  createTeamMember: (data) => api.post('/team/admin/members', data),
  updateTeamMember: (id, data) => api.put(`/team/admin/members/${id}`, data),
  deleteTeamMember: (id) => api.delete(`/team/admin/members/${id}`),
  reorderTeamMembers: (data) => api.put('/team/admin/members-order', data),

  // Careers (Join Our Team)
  getCareers: () => api.get('/careers/admin'),
  updateCareersHero: (data) => api.put('/careers/admin/hero', data),
  updateCareersWhy: (data) => api.put('/careers/admin/why', data),
  updateCareersLife: (data) => api.put('/careers/admin/life', data),
  updateCareersPositionsHeadings: (data) => api.put('/careers/admin/positions-headings', data),
  getJobCategories: () => api.get('/careers/admin/categories'),
  createJobCategory: (data) => api.post('/careers/admin/categories', data),
  updateJobCategory: (id, data) => api.put(`/careers/admin/categories/${id}`, data),
  deleteJobCategory: (id) => api.delete(`/careers/admin/categories/${id}`),
  createJob: (categoryId, data) => api.post(`/careers/admin/categories/${categoryId}/jobs`, data),
  updateJob: (id, data) => api.put(`/careers/admin/jobs/${id}`, data),
  deleteJob: (id) => api.delete(`/careers/admin/jobs/${id}`),
  getApplicants: (params) => api.get('/careers/admin/applicants', { params }),
  downloadApplicantResume: (id) =>
    api.get(`/careers/admin/applicants/${id}/resume`, { responseType: 'blob' }),
  updateApplicantStatus: (id, data) => api.put(`/careers/admin/applicants/${id}`, data),

  // Bookings / Registrations
  getBookings: (params) => api.get('/bookings/admin/list', { params }),
  /** UTF-8 CSV (opens in Excel); same filters as list, up to ~25k rows. */
  exportRegistrationsCsv: (params) =>
    api.get('/bookings/admin/export', { params, responseType: 'blob' }),
  getTicketBookings: (params) => api.get('/bookings/admin/tickets', { params }),
  /** Legacy ticket spreadsheet CSV — all rows matching Tickets list filters. */
  exportTicketsCsvByFilters: (params) =>
    api.get('/bookings/admin/tickets/export-csv', { params, responseType: 'blob' }),
  getReceipts: (params) => api.get('/bookings/admin/receipts', { params }),
  exportReceiptsCsv: (params) =>
    api.get('/bookings/admin/receipts/export', { params, responseType: 'blob' }),
  getManualReceipts: (params) => api.get('/bookings/admin/manual-receipts', { params }),
  getNextManualReceiptId: () => api.get('/bookings/admin/manual-receipts/next-id'),
  createManualReceipt: (data) => api.post('/bookings/admin/manual-receipts', data),
  updateReceipt: (receiptId, data) => api.patch(`/bookings/admin/receipts/${receiptId}`, data),
  addBookingReceipt: (bookingId, data) => api.post(`/bookings/admin/${bookingId}/receipts`, data),
  getBooking: (bookingId) => api.get(`/bookings/${bookingId}`),
  patchAdminBooking: (bookingId, data) => api.patch(`/bookings/admin/${bookingId}`, data),
  listEventRegistrationGroups: (eventId) =>
    api.get('/bookings/admin/event-groups', { params: { eventId } }),
  /** Same event only; min two booking UUIDs. */
  createRegistrationGroupFromBookings: (data) =>
    api.post('/bookings/admin/registration-group', data),
  uploadBookingTicketFile: (bookingId, formData) =>
    api.post(`/bookings/admin/${bookingId}/ticket-files`, formData),
  updateBookingTicketLegPnr: (bookingId, data) =>
    api.patch(`/bookings/admin/${bookingId}/ticket-pnr`, data),
  deleteBookingTicketFile: (bookingId, fileId) =>
    api.delete(`/bookings/admin/${bookingId}/ticket-files`, { params: { fileId } }),
  downloadBookingTicketFile: (bookingId, params = {}) =>
    api.get(`/bookings/${bookingId}/ticket-download`, {
      params,
      responseType: 'blob'
    }),
  getCancellations: (params) => api.get('/cancellations/admin/list', { params }),
  getCancellationById: (id) => api.get(`/cancellations/admin/${id}`),
  downloadCancellationDocument: (id) =>
    api.get(`/cancellations/admin/${id}/document`, { responseType: 'blob' }),
  patchCancellation: (id, data) => api.patch(`/cancellations/admin/${id}`, data)
};

export const contentAPI = {
  uploadPdf: (file, options) => adminAPI.uploadPdf(file, options),
  // Hero Sliders
  getHeroSliders: () => api.get('/content/hero-sliders'),
  createHeroSlider: (data) => api.post('/content/hero-sliders', data),
  updateHeroSlider: (id, data) => api.put(`/content/hero-sliders/${id}`, data),
  deleteHeroSlider: (id) => api.delete(`/content/hero-sliders/${id}`),
  
  // Highlighted Events (homepage strip)
  getHighlightedEvents: () => api.get('/content/highlighted-events'),
  createHighlightedEvent: (data) => api.post('/content/highlighted-events', data),
  updateHighlightedEvent: (id, data) => api.put(`/content/highlighted-events/${id}`, data),
  deleteHighlightedEvent: (id) => api.delete(`/content/highlighted-events/${id}`),
  reorderHighlightedEvents: (ids) => api.put('/content/highlighted-events/reorder', { ids }),
  
  // Stats
  getStats: () => api.get('/content/stats'),
  updateStats: (data) => api.put('/content/stats', data),
  
  // Events
  getEvents: () => api.get('/content/events'),
  getEventBySlug: (slug) => api.get(`/content/events/${slug}`),
  createEvent: (data) => api.post('/content/events', data),
  updateEvent: (id, data) => api.put(`/content/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/content/events/${id}`),
  
  // Blogs
  getBlogs: (params = {}) =>
    api.get('/content/blogs', { params: { limit: 200, includeDrafts: true, ...params } }),
  getBlogById: (id) => api.get(`/content/blogs/${id}`),
  createBlog: (data) => api.post('/content/blogs', data),
  updateBlog: (id, data) => api.put(`/content/blogs/${id}`, data),
  deleteBlog: (id) => api.delete(`/content/blogs/${id}`),
  
  // Testimonials
  getTestimonials: () => api.get('/content/testimonials'),
  createTestimonial: (data) => api.post('/content/testimonials', data),
  updateTestimonial: (id, data) => api.put(`/content/testimonials/${id}`, data),
  deleteTestimonial: (id) => api.delete(`/content/testimonials/${id}`),
  
  // Partners
  getPartners: () => api.get('/content/partners'),
  createPartner: (data) => api.post('/content/partners', data),
  updatePartner: (id, data) => api.put(`/content/partners/${id}`, data),
  deletePartner: (id) => api.delete(`/content/partners/${id}`),
  
  // Stats
  createStat: (data) => api.post('/content/stats', data),
  deleteStat: (id) => api.delete(`/content/stats/${id}`),
  
  // Login Page Images
  getLoginPageImages: () => api.get('/content/login-page-images'),
  createLoginPageImage: (data) => api.post('/content/login-page-images', data),
  updateLoginPageImage: (id, data) => api.put(`/content/login-page-images/${id}`, data),
  deleteLoginPageImage: (id) => api.delete(`/content/login-page-images/${id}`),
  
  // Site Settings (section labels)
  getSiteSettings: () => api.get('/content/site-settings'),
  updateSiteSettings: (settings) => api.put('/content/site-settings', { settings }),

  // Expedition page (title, subtitle, trek list)
  getExpeditionSection: () => api.get('/content/expedition-section'),
  getTreksWithUpcomingBatches: () => api.get('/content/treks-with-upcoming-batches'),
  updateExpeditionSection: (data) => api.put('/content/expedition-section', data),

  // Home categories (experience/special) and slug-based trek mapping
  getHomeCategories: (params) => api.get('/home-categories', { params }),
  createHomeCategory: (data) => api.post('/home-categories', data),
  updateHomeCategory: (id, data) => api.put(`/home-categories/${id}`, data),
  deleteHomeCategory: (id) => api.delete(`/home-categories/${id}`),
  createHomeSubcategory: (categoryId, data) => api.post(`/home-categories/${categoryId}/subcategories`, data),
  updateHomeSubcategory: (categoryId, subcategoryId, data) => api.put(`/home-categories/${categoryId}/subcategories/${subcategoryId}`, data),
  deleteHomeSubcategory: (categoryId, subcategoryId) => api.delete(`/home-categories/${categoryId}/subcategories/${subcategoryId}`),
  getHomeCategoryTreks: (categoryId, params) => api.get(`/home-categories/${categoryId}/treks`, { params }),
  setHomeCategoryTreks: (categoryId, data) => api.put(`/home-categories/${categoryId}/treks`, data),

  // Callback requests
  getCallbackRequests: (params) => api.get('/content/callback-requests', { params }),
  updateCallbackRequest: (id, data) => api.put(`/content/callback-requests/${id}`, data),
  getVolunteerInquiries: (params) => api.get('/content/volunteer-inquiries', { params }),
  updateVolunteerInquiry: (id, data) => api.put(`/content/volunteer-inquiries/${id}`, data),
  getPdfRequests: (params) => api.get('/content/pdf-requests', { params }),
  updatePdfRequest: (id, data) => api.put(`/content/pdf-requests/${id}`, data),
};

export const crmAPI = {
  listLeads: (params) => api.get('/crm/leads', { params }),
  exportLeadsCsv: (params) => api.get('/crm/leads/export', { params, responseType: 'blob' }),
  getReasons: () => api.get('/crm/reasons'),
  lookupPhone: (phone) => api.get('/crm/lookup', { params: { phone } }),
  createLead: (data) => api.post('/crm/leads', data),
  updateLead: (leadType, id, data) => api.put(`/crm/leads/${leadType}/${id}`, data),
  getLeadActivity: (leadType, id, params) => api.get(`/crm/leads/${leadType}/${id}/activity`, { params }),
  appendLeadNote: (leadType, id, data) => api.post(`/crm/leads/${leadType}/${id}/notes`, data),
  getAssignees: () => api.get('/crm/assignees'),
  getReports: (params) => api.get('/crm/reports', { params }),
  createSupportTicket: (data) => api.post('/crm/support-tickets', data),
  listSupportTickets: (params) => api.get('/crm/support-tickets', { params }),
  updateSupportTicket: (id, data) => api.put(`/crm/support-tickets/${id}`, data),
  incomingPopups: () => api.get('/crm/incoming/popups'),
  acceptCall: (id) => api.post(`/crm/leads/${id}/accept`),
  popupSeen: (id) => api.post(`/crm/leads/${id}/popup-seen`),
  saveLead: (id, data) => api.patch(`/crm/leads/${id}`, data),
  sendLeadWhatsAppTemplate: (data) => api.post('/crm/leads/waplify/send', data),
  clickToCallLead: (data) => api.post('/crm/leads/click-to-call', data),
};

export const categoryAPI = {
  list: (params) => api.get('/categories', { params }),
  get: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  remove: (id) => api.delete(`/categories/${id}`)
};

export const eventTypeAPI = {
  list: (params) => api.get('/event-types', { params }),
  create: (data) => api.post('/event-types', data),
  update: (id, data) => api.put(`/event-types/${id}`, data),
  remove: (id) => api.delete(`/event-types/${id}`)
};

export const locationAPI = {
  list: (params) => api.get('/locations', { params }),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  remove: (id) => api.delete(`/locations/${id}`)
};

export const refundCalculatorCategoryAPI = {
  list: (params) => api.get('/refund-calculator-categories', { params }),
  create: (data) => api.post('/refund-calculator-categories', data),
  update: (id, data) => api.put(`/refund-calculator-categories/${id}`, data),
  remove: (id) => api.delete(`/refund-calculator-categories/${id}`)
};

export const attractionAPI = {
  list: (params) => api.get('/attractions', { params }),
  get: (id) => api.get(`/attractions/${id}`),
  create: (data) => api.post('/attractions', data),
  update: (id, data) => api.put(`/attractions/${id}`, data),
  remove: (id) => api.delete(`/attractions/${id}`)
};

export const trekAPI = {
  list: (params) => api.get('/treks', { params }),
  get: (id) => api.get(`/treks/${id}`),
  create: (data) => api.post('/treks', data),
  update: (id, data) => api.put(`/treks/${id}`, data),
  updateLeadTemplates: (id, data) => api.put(`/treks/${id}/lead-templates`, data),
  remove: (id) => api.delete(`/treks/${id}`),

  listPackages: (trekId) => api.get(`/treks/${trekId}/packages`),
  createPackage: (trekId, data) => api.post(`/treks/${trekId}/packages`, data),
  updatePackage: (trekId, id, data) => api.put(`/treks/${trekId}/packages/${id}`, data),
  setPackageMain: (trekId, packageId) => api.put(`/treks/${trekId}/packages/${packageId}/set-main`),
  deletePackage: (trekId, id) => api.delete(`/treks/${trekId}/packages/${id}`),

  listScheduleDays: (trekId) => api.get(`/treks/${trekId}/schedule-days`),
  createScheduleDay: (trekId, data) => api.post(`/treks/${trekId}/schedule-days`, data),
  updateScheduleDay: (trekId, id, data) => api.put(`/treks/${trekId}/schedule-days/${id}`, data),
  deleteScheduleDay: (trekId, id) => api.delete(`/treks/${trekId}/schedule-days/${id}`),

  listAttractions: (trekId) => api.get(`/treks/${trekId}/attractions`),
  createAttraction: (trekId, data) => api.post(`/treks/${trekId}/attractions`, data),
  updateAttraction: (trekId, id, data) => api.put(`/treks/${trekId}/attractions/${id}`, data),
  deleteAttraction: (trekId, id) => api.delete(`/treks/${trekId}/attractions/${id}`),

  listFeesIncludes: (trekId) => api.get(`/treks/${trekId}/fees-includes`),
  createFeeInclude: (trekId, data) => api.post(`/treks/${trekId}/fees-includes`, data),
  updateFeeInclude: (trekId, id, data) => api.put(`/treks/${trekId}/fees-includes/${id}`, data),
  deleteFeeInclude: (trekId, id) => api.delete(`/treks/${trekId}/fees-includes/${id}`),

  listInstructions: (trekId) => api.get(`/treks/${trekId}/instructions`),
  createInstruction: (trekId, data) => api.post(`/treks/${trekId}/instructions`, data),
  updateInstruction: (trekId, id, data) => api.put(`/treks/${trekId}/instructions/${id}`, data),
  deleteInstruction: (trekId, id) => api.delete(`/treks/${trekId}/instructions/${id}`),

  listMedia: (trekId) => api.get(`/treks/${trekId}/media`),
  createMedia: (trekId, data) => api.post(`/treks/${trekId}/media`, data),
  updateMedia: (trekId, id, data) => api.put(`/treks/${trekId}/media/${id}`, data),
  deleteMedia: (trekId, id) => api.delete(`/treks/${trekId}/media/${id}`),
  uploadImage: async (file) => {
    const processedFile = await compressImageIfNeeded(file);
    const formData = new FormData();
    formData.append('image', processedFile);
    return api.post('/uploads/images', formData);
  },

  listVideos: (trekId) => api.get(`/treks/${trekId}/videos`),
  createVideo: (trekId, data) => api.post(`/treks/${trekId}/videos`, data),
  updateVideo: (trekId, id, data) => api.put(`/treks/${trekId}/videos/${id}`, data),
  deleteVideo: (trekId, id) => api.delete(`/treks/${trekId}/videos/${id}`),

  listExperienceGallery: (trekId) => api.get(`/treks/${trekId}/experience-gallery`),
  createExperienceGalleryItem: (trekId, data) => api.post(`/treks/${trekId}/experience-gallery`, data),
  updateExperienceGalleryItem: (trekId, id, data) => api.put(`/treks/${trekId}/experience-gallery/${id}`, data),
  deleteExperienceGalleryItem: (trekId, id) => api.delete(`/treks/${trekId}/experience-gallery/${id}`),

  listWebstories: (trekId) => api.get(`/treks/${trekId}/webstories`),
  createWebstory: (trekId, data) => api.post(`/treks/${trekId}/webstories`, data),
  updateWebstory: (trekId, id, data) => api.put(`/treks/${trekId}/webstories/${id}`, data),
  deleteWebstory: (trekId, id) => api.delete(`/treks/${trekId}/webstories/${id}`),
};

export const eventBatchAPI = {
  list: (params) => api.get('/event-batches', { params }),
  get: (id) => api.get(`/event-batches/${id}`),
  create: (data) => api.post('/event-batches', data),
  update: (id, data) => api.put(`/event-batches/${id}`, data),
  remove: (id) => api.delete(`/event-batches/${id}`),
  listUpcomingTreks: (params) => api.get('/event-batches/upcoming/treks', { params })
};

export const specialEventAPI = {
  list: (params) => api.get('/special-events', { params }),
  get: (id) => api.get(`/special-events/${id}`),
  create: (data) => api.post('/special-events', data),
  update: (id, data) => api.put(`/special-events/${id}`, data),
  remove: (id) => api.delete(`/special-events/${id}`),
  setEnabledTreks: (id, trekIds) => api.put(`/special-events/${id}/enabled-treks`, { trekIds }),

  listImages: (id) => api.get(`/special-events/${id}/images`),
  createImage: (id, data) => api.post(`/special-events/${id}/images`, data),
  updateImage: (id, nestedId, data) => api.put(`/special-events/${id}/images/${nestedId}`, data),
  deleteImage: (id, nestedId) => api.delete(`/special-events/${id}/images/${nestedId}`),

  listVideos: (id) => api.get(`/special-events/${id}/videos`),
  createVideo: (id, data) => api.post(`/special-events/${id}/videos`, data),
  updateVideo: (id, nestedId, data) => api.put(`/special-events/${id}/videos/${nestedId}`, data),
  deleteVideo: (id, nestedId) => api.delete(`/special-events/${id}/videos/${nestedId}`),

  listStories: (id) => api.get(`/special-events/${id}/stories`),
  createStory: (id, data) => api.post(`/special-events/${id}/stories`, data),
  updateStory: (id, nestedId, data) => api.put(`/special-events/${id}/stories/${nestedId}`, data),
  deleteStory: (id, nestedId) => api.delete(`/special-events/${id}/stories/${nestedId}`)
};

export const addOnAPI = {
  list: (params) => api.get('/add-ons/admin/list', { params }),
  get: (id) => api.get(`/add-ons/${id}`),
  create: (data) => api.post('/add-ons/admin', data),
  update: (id, data) => api.put(`/add-ons/admin/${id}`, data),
  remove: (id) => api.delete(`/add-ons/admin/${id}`)
};

export const shortLinkAPI = {
  list: (params) => api.get('/short-links', { params }),
  create: (data) => api.post('/short-links', data),
  update: (id, data) => api.put(`/short-links/${id}`, data),
  remove: (id) => api.delete(`/short-links/${id}`)
};

export const trainingCampAPI = {
  listSubmissions: (params) => api.get('/training-camp/admin/submissions', { params }),
  updateSubmission: (id, data) => api.put(`/training-camp/admin/submissions/${id}`, data),
  listSlots: (params) => api.get('/training-camp/admin/slots', { params }),
  createSlots: (data) => api.post('/training-camp/admin/slots', data),
  updateSlot: (id, data) => api.put(`/training-camp/admin/slots/${id}`, data),
  deleteSlot: (id) => api.delete(`/training-camp/admin/slots/${id}`)
};

export default api;

